import { Injectable } from '@angular/core';
import { ILoopByTrain } from 'src/app/modules/widget/interfaces/loop-by-train';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { environment } from 'src/environments/environment';
import { getMoment } from 'src/app/modules/core/services/datetime.service';
import { groupBy, hexToRGB, sortByKey } from 'projects/common/src/app/services/utils/utilities';
import { IChartSubtitle } from 'src/app/modules/widget/interfaces/chart-subtitle';

@Injectable({ providedIn: 'root' })
export class LoopsByTrainService {
  constructor(private langService: LanguageService, private datePipe: DatePresentationPipe) {}

  /**
   * Function to create a serie data for chart
   */
  public createSerieData(loops: ILoopByTrain[]): any {
    let shifts = loops.map((value) => value.shiftName);
    shifts = shifts.filter((v, i) => shifts.indexOf(v) === i);
    shifts.sort();
    shifts.reverse();

    const series = [];

    for (const shift of shifts) {
      // Buscar si existe algún dato para esa máquina
      const machineData = loops.filter((value) => value.shiftName === shift);

      for (const machine of machineData) {
        const found = series.find((value) => value.name === machine.shiftName);

        if (found) {
          found.data.push({
            machineName: machine.machineName,
            inside: machine.lastInTransit,
            outside: machine.lastOutTransit,
            loopsByShift: loops.filter((value) => value.machineId === machine.machineId),
            isDayDetail: shifts.length > 1,
            y: machine.loops,
          });
        } else {
          series.push({
            name: machine.shiftName,
            color: hexToRGB(machine.shiftColor, '0.5') || hexToRGB('#707070', '0.5'),
            borderColor: machine.shiftColor || '#707070',
            data: [
              {
                machineName: machine.machineName,
                inside: machine.lastInTransit,
                outside: machine.lastOutTransit,
                loopsByShift: loops.filter((value) => value.machineId === machine.machineId),
                isDayDetail: shifts.length > 1,
                y: machine.loops,
              },
            ],
          });
        }
      }
    }

    return series;
  }

  /**
   * Function to get chart categories
   *
   * @param loops data from end point
   */
  public getCategories(loops: ILoopByTrain[]): string[] {
    let categories = loops.map((value) => value.machineName);
    categories = categories.filter((v, i) => categories.indexOf(v) === i);

    return categories;
  }

  /**
   * Function to display the legend box
   *
   * @param shift Current shift
   */
  public styleShiftLegend(shift: any): any {
    return {
      'background-color': hexToRGB(shift.shiftColor, '0.5'),
      border: `2px solid ${shift.shiftColor}`,
    };
  }

  /**
   * Construye la data que pintará el gráfico
   *
   * @param categories categorias del gráfico
   * @param series series del gráfico
   * @param chartSubtitle Objeto que contiene user, machineType, date, shift
   */
  public getCharData(categories, series, chartSubtitle: IChartSubtitle): any {
    const data = {
      chart: {
        type: 'column',
        zoomType: 'xy',
        backgroundColor: '#F8F8F8',
      },
      title: {
        text: '',
        margin: 50,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories,
      },
      subtitle: {
        text: `${this.langService.message('company')}: ${chartSubtitle.user.company.name},
                      ${this.langService.message('device')}: ${this.langService.message(chartSubtitle.machineType.name)},
                      ${this.langService.message('date')}: ${this.datePipe.transform(chartSubtitle.date)},
                      ${this.langService.message('shift_')}: ${chartSubtitle.shift.name}`,
      },
      yAxis: {
        min: 0,
        title: {
          text: this.langService.message('loops'),
        },
        stackLabels: {
          enabled: true,
          style: {
            color: '#707070',
            fontWeight: 'bold',
          },
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        backgroundColor: '#433e3e',
        borderColor: '#433e3e',
        borderRadius: 2,
        style: {
          color: 'white',
        },
        useHTML: true,
        formatter() {
          const format = `${environment.dateFormatSystem} ${environment.timeFormat}`;

          let html = `<div style="width: 130px">
                <strong>ÚLTIMA VUELTA</strong> <br>
                <span>${this.point.machineName}</span> <br>
                <span>Entrada: ${
                  this.point.inside !== '0001-01-01T00:00:00+00:00' ? getMoment(this.point.inside, format).format('HH:mm:ss') : '-'
                }</span> <br>
                <span>Salida: ${
                  this.point.outside !== '0001-01-01T00:00:00+00:00' ? getMoment(this.point.outside, format).format('HH:mm:ss') : '-'
                }</span> <br>`;

          if (this.point.options.isDayDetail) {
            this.point.options.loopsByShift = sortByKey(this.point.options.loopsByShift, 'shiftName');
            for (const shiftDetail of this.point.options.loopsByShift as ILoopByTrain[]) {
              html += `<span>Vueltas T. ${shiftDetail.shiftName}: ${shiftDetail.loops}</span> <br>`;
            }
          } else {
            html += `<span>Vueltas: ${this.y}</span> <br>`;
          }

          html += `</div>`;

          return html;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series,
    };

    return data;
  }
}
